import { type IReactionDisposer, makeAutoObservable, reaction, runInAction } from 'mobx'
import { debounce } from 'lodash'
import { isCancel } from 'axios'
import type { ITabItem } from 'shared/ui'
import { logger, removeSpacesFromNumericString } from 'shared/lib'
import { RequestCancelAxios } from 'shared/api/lib'
import { IntegrationKey } from 'entities/Integrations'
import type { IEntitiesStore } from 'entities/EntitiesExport'
import { ContactsApi, type IContactAll } from 'entities/Contacts'
import { type IResponseContactAll } from 'entities/Contacts/api/types'
import { ResultsContactsByIntegrationStore } from './ResultsContactsByIntegrationStore'
import { type IResultsAllIntegrations } from './types'

const LIST = [
  IntegrationKey.salesmsg,
  IntegrationKey.hubspot,
  IntegrationKey.salesforce,
  IntegrationKey.activecampaign,
  IntegrationKey.infusionsoft,
  IntegrationKey.pipedrive,
  IntegrationKey.zoho,
  IntegrationKey.integrately,
  IntegrationKey.outreach,
  IntegrationKey.close,
]

const MIX_LIMIT_TOP_RESULT = 8

type IResultsContactsStoreConfig = {
  onCount: (value: number) => void
}

export class ResultsContactsStore {
  private _allIntegrationsMap: Map<IntegrationKey, IResultsAllIntegrations> = new Map()
  private _tabsMap: Map<IntegrationKey, ITabItem<IntegrationKey>> = new Map()
  private _activeTab: IntegrationKey = IntegrationKey.salesmsg
  private _disposeCount: IReactionDisposer | null = null
  private _itemsTopResultMap: Map<IResponseContactAll['id'], IContactAll> = new Map()
  private _loadingTopResult = false
  private _cancelTopResultRequest = new RequestCancelAxios()

  readonly debounceLoadContacts: ReturnType<typeof debounce>

  constructor(private _entities: IEntitiesStore, private _config: IResultsContactsStoreConfig) {
    makeAutoObservable(this)

    this.debounceLoadContacts = debounce(this.loadContacts, 500)
    this.initIntegrations()
    this.initTabs()
  }

  initIntegrations = () => {
    LIST.forEach((key) => {
      this._allIntegrationsMap.set(key, {
        key: key,
        store: new ResultsContactsByIntegrationStore(key, this._entities),
      })
    })
  }

  initTabs = () => {
    LIST.forEach((key) => {
      if (key === IntegrationKey.salesmsg) {
        this._tabsMap.set(key, {
          key: key,
          name: 'Salesmsg',
          count: 0,
        })

        return
      }

      const integration = this._entities.integrationsStore.getIntegration(key)

      if (integration) {
        this._tabsMap.set(key, {
          key: key,
          name: integration.name,
          count: 0,
        })
      }
    })
  }

  updateCountTab = (key: IntegrationKey, count: number) => {
    const tab = this._tabsMap.get(key)

    if (tab) {
      this._tabsMap.set(key, {
        ...tab,
        count: count,
      })
    }
  }

  reactionCount = () => {
    this._disposeCount?.()
    this._disposeCount = reaction(
      () => this.count,
      (value) => {
        this._config.onCount(value)
      }
    )
  }

  fetchContacts = (search: string) => {
    runInAction(() => {
      this._loadingTopResult = true
    })

    this.allowedIntegrations.forEach((item) => {
      const store = item.store

      runInAction(() => {
        store.setLoading(true)
      })
    })

    this.debounceLoadContacts(search)
  }

  loadContacts = (search: string) => {
    this.loadContactsTopSearch(search)

    this.allowedIntegrations.forEach((item) => {
      const store = item.store
      const key = item.key

      store.cancelRequest.init()
      store
        .fetch({
          search: search,
        })
        .then(() => {
          this.updateCountTab(key, store.total)
        })
    })
  }

  loadContactsTopSearch = async (search: string) => {
    try {
      runInAction(() => {
        this._loadingTopResult = true
      })

      this._itemsTopResultMap.clear()
      this._cancelTopResultRequest.init()
      const response = await ContactsApi.getContactsTopSearch(
        {
          limit: 8,
          term: removeSpacesFromNumericString(search),
        },
        {
          cancelToken: this._cancelTopResultRequest.token,
        }
      )
      const data = response.data

      runInAction(() => {
        data.results.forEach((item) => {
          const contact = this._entities.contactsStore.createContactByResponse(item)
          if (contact) this._itemsTopResultMap.set(item.id, contact)
        })
      })

      runInAction(() => {
        this._loadingTopResult = false
      })
    } catch (e) {
      if (!isCancel(e)) {
        runInAction(() => {
          this._loadingTopResult = false
        })
      }

      logger.error(e)
    }
  }

  resetCancelTokenSource = () => {
    this.allowedIntegrations.forEach(({ store }) => {
      store.cancelRequest.reset()
    })
  }

  init = () => {
    this.reactionCount()
  }

  dispose = () => {
    this._allIntegrationsMap.forEach(({ store }) => {
      store.dispose()
    })
    this._activeTab = IntegrationKey.salesmsg

    this.resetCancelTokenSource()
    this._disposeCount?.()
    this.debounceLoadContacts?.cancel()

    this._itemsTopResultMap.clear()
    this._loadingTopResult = false
    this._cancelTopResultRequest.reset()
  }

  setActiveTab = (key: IntegrationKey) => {
    this._activeTab = key
  }

  get loading() {
    return this.allowedIntegrations.some((item) => item.store.loading)
  }

  get loadingTopResult() {
    return this._loadingTopResult
  }

  get allowedIntegrationsKeys() {
    return this.allowedIntegrations.map((item) => item.key)
  }

  get allowedIntegrations() {
    const items = Array.from(this._allIntegrationsMap.values())

    return items.filter((item) => {
      if (item.key === IntegrationKey.salesmsg) return true

      const integration = this._entities.integrationsStore.getIntegration(item.key)

      return integration?.isConnected
    })
  }

  get itemsTopResult() {
    const items = Array.from(this._itemsTopResultMap.values())

    if (items.length > MIX_LIMIT_TOP_RESULT) {
      return items.filter((item, index) => index + 1 <= MIX_LIMIT_TOP_RESULT)
    }

    return items
  }

  get count() {
    return this.allowedIntegrations.reduce<number>((state, item) => {
      const total = item.store.total

      state += total

      return state
    }, 0)
  }

  get hasItemsTopResult() {
    return Boolean(this._itemsTopResultMap.size)
  }

  get tabs() {
    return this.allowedIntegrationsKeys.reduce<ITabItem<IntegrationKey>[]>((state, key) => {
      const tab = this._tabsMap.get(key)

      if (tab) {
        state.push(tab)
      }

      return state
    }, [])
  }

  get activeTab() {
    return this._activeTab
  }

  get activeContactsByIntegration() {
    return this._allIntegrationsMap.get(this.activeTab)
  }
}
